import type { Level, LevelId } from "./types";
import { aprendizExercises } from "./exercises/aprendiz";
import { exploradorExercises } from "./exercises/explorador";
import { intermedioExercises } from "./exercises/intermedio";
import { avanzadoExercises } from "./exercises/avanzado";
import { virtuosoExercises } from "./exercises/virtuoso";

export const trainingPlan: Level[] = [
  {
    id: "aprendiz",
    name: "Nivel 1: Aprendiz",
    tagline: "Los fundamentos que nadie te enseño bien",
    description:
      "Antes de correr hay que aprender a caminar. Este nivel cubre la postura, la afinacion, la digitacion basica y la articulacion del pulgar. Inspirado en los Capitulos I-IV del Metodo de Guitarra Chilena.",
    methodChapter: "Cap. I-IV: Conocimientos preliminares, Digitacion, Pulgar",
    color: "#22c55e",
    bgGradient: "from-green-500/10 to-emerald-500/5",
    requiredExercises: 11,
    exercises: aprendizExercises,
  },
  {
    id: "explorador",
    name: "Nivel 2: Explorador",
    tagline: "Cartografia completa del mastil",
    description:
      "Ahora que conoces las reglas, es hora de explorar el terreno. Las cuerdas agudas y graves, los equisonos (misma nota, distinto lugar) y el pulgar articulado. Basado en los Capitulos V-VII y XIII del Metodo.",
    methodChapter: "Cap. V-VII, XIII: Cuerdas agudas/graves, Equisonos, Pulgar articulado",
    color: "#3b82f6",
    bgGradient: "from-blue-500/10 to-cyan-500/5",
    requiredExercises: 5,
    exercises: exploradorExercises,
  },
  {
    id: "intermedio",
    name: "Nivel 3: Intermedio",
    tagline: "Arpegios, cromatismo y coordinacion total",
    description:
      "El nivel donde todo empieza a conectar. Arpegios sin apoyo, escala cromatica, desplazamientos de mano y cuerdas dobles. Extraido de los Capitulos VIII-XII del Metodo de Guitarra Chilena.",
    methodChapter: "Cap. VIII-XII: Arpegios, Cromatismo, Desplazamientos, Cuerdas dobles",
    color: "#f59e0b",
    bgGradient: "from-amber-500/10 to-yellow-500/5",
    requiredExercises: 5,
    exercises: intermedioExercises,
  },
  {
    id: "avanzado",
    name: "Nivel 4: Avanzado",
    tagline: "Escalas, ceja y arpegios: el mastil se abre",
    description:
      "La ceja (cejilla) es el gran salto: te permite tocar en cualquier posicion. Combinada con las escalas mayores y los arpegios con pulgar, abre el mastil completo. Del Capitulo XIV-XVI.",
    methodChapter: "Cap. XIV-XVI: Escalas mayores, Ceja, Arpegios con pulgar",
    color: "#f97316",
    bgGradient: "from-orange-500/10 to-red-500/5",
    requiredExercises: 4,
    exercises: avanzadoExercises,
  },
  {
    id: "virtuoso",
    name: "Nivel 5: Virtuoso",
    tagline: "Ligados, intervalos y efectos especiales",
    description:
      "El nivel final. Notas ligadas, terceras y sextas melodicas, armónicos naturales y tambora. Las tecnicas avanzadas que distinguen a los maestros del folclor chileno. Capitulos XVII-XX.",
    methodChapter: "Cap. XVII-XX: Ligados, Terceras/Sextas, Armónicos, Tambora",
    color: "#dc2626",
    bgGradient: "from-red-500/10 to-rose-500/5",
    requiredExercises: 5,
    exercises: virtuosoExercises,
  },
];

export const getAllExercises = () => trainingPlan.flatMap((level) => level.exercises);

export const getLevelById = (id: LevelId) => trainingPlan.find((level) => level.id === id);

export const getExerciseById = (id: string) => getAllExercises().find((ex) => ex.id === id);

const CATEGORY_LABELS: Record<string, string> = {
  notas: "Notas",
  digitacion: "Digitacion",
  arpegios: "Arpegios",
  escalas: "Escalas",
  teoria: "Teoria Musical",
  ritmo: "Ritmo",
  efectos: "Efectos Especiales",
};

const DIFFICULTY_LABELS: Record<string, string> = {
  facil: "Principiante",
  medio: "Intermedio",
  dificil: "Avanzado",
  experto: "Experto",
};

const TYPE_LABELS: Record<string, string> = {
  quiz: "Cuestionario",
  click: "Interactivo",
  sequence: "Secuencia",
  practice: "Practica",
};

export const getCategoryLabel = (cat: string) => CATEGORY_LABELS[cat] ?? cat;
export const getDifficultyLabel = (d: string) => DIFFICULTY_LABELS[d] ?? d;
export const getTypeLabel = (t: string) => TYPE_LABELS[t] ?? t;

export { aprendizExercises, exploradorExercises, intermedioExercises, avanzadoExercises, virtuosoExercises };
